import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, RefreshCw, X } from 'lucide-react';
import {
  useWebsiteLeads,
  useWebsiteLead,
  useUpdateWebsiteLead,
  useMarkWebsiteLeadSpam,
  useCloseWebsiteLead,
  useSendWebsiteLeadEmail,
} from '../../api/hooks';
import { SectionHeader, Spinner, Tabs } from '../../components/ui';
import AdminStrategyRequests from './AdminStrategyRequests';
import { toast } from '../../store';
import { cn, fmtDateTime } from '../../lib/utils';

const PAGE_TABS = [
  { id: 'website-leads', label: 'Website Leads' },
  { id: 'strategy-requests', label: 'Strategy Requests' },
];

const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED', 'CLOSED', 'SPAM'];
const PAGE_SIZE = 50;
const DEFAULT_EMAIL_SUBJECT = 'Re: Your AlgoSys enquiry';

const STATUS_STYLE = {
  NEW:       'text-info   bg-info/10   border-info/30',
  CONTACTED: 'text-warn   bg-warn/10   border-warn/30',
  QUALIFIED: 'text-bull   bg-bull/10   border-bull/30',
  CONVERTED: 'text-bull   bg-bull/10   border-bull/30',
  CLOSED:    'text-txt-muted bg-bg-surface border-bg-border',
  SPAM:      'text-bear   bg-bear/10   border-bear/30',
};

function StatusBadge({ status }) {
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono',
      STATUS_STYLE[status] ?? 'text-txt-muted border-bg-border',
    )}>
      {status ?? '—'}
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{label}</span>
      <div className="text-xs text-txt-primary break-words">{value ?? <span className="text-txt-muted">—</span>}</div>
    </div>
  );
}

function LeadDrawer({ leadId, onClose }) {
  const { data: lead, isLoading, error, refetch } = useWebsiteLead(leadId);
  const updateLead = useUpdateWebsiteLead();
  const markSpam   = useMarkWebsiteLeadSpam();
  const closeLead  = useCloseWebsiteLead();
  const sendEmail  = useSendWebsiteLeadEmail();

  const [form, setForm] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: DEFAULT_EMAIL_SUBJECT, message: '' });

  // Sync form when lead loads or changes
  useEffect(() => {
    if (lead) {
      setForm({
        status:         lead.status,
        internal_notes: lead.internal_notes ?? '',
        follow_up_at:   lead.follow_up_at ? lead.follow_up_at.slice(0, 10) : '',
      });
      setEmailForm({ subject: DEFAULT_EMAIL_SUBJECT, message: '' });
    }
  }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!leadId) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await updateLead.mutateAsync({
        id: leadId,
        payload: {
          status:         form.status,
          internal_notes: form.internal_notes || null,
          follow_up_at:   form.follow_up_at || null,
        },
      });
      toast.success('Lead updated');
    } catch (err) {
      toast.error(err?.message || 'Failed to update lead');
    }
  };

  const handleMarkSpam = async () => {
    try {
      await markSpam.mutateAsync(leadId);
      toast.success('Marked as spam');
    } catch (err) {
      toast.error(err?.message || 'Failed to mark spam');
    }
  };

  const handleClose = async () => {
    try {
      await closeLead.mutateAsync(leadId);
      toast.success('Lead closed');
    } catch (err) {
      toast.error(err?.message || 'Failed to close lead');
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    try {
      await sendEmail.mutateAsync({
        id: leadId,
        payload: {
          subject: emailForm.subject,
          message: emailForm.message,
        },
      });
      toast.success('Email sent');
      setEmailForm({ subject: DEFAULT_EMAIL_SUBJECT, message: '' });
    } catch (err) {
      toast.error(err?.message || 'Failed to send email');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full md:max-w-[min(1080px,78vw)] bg-bg-panel border-l border-bg-border flex flex-col animate-slide-in shadow-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-bg-border shrink-0">
          <div>
            <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-0.5">Lead Detail</p>
            <h3 className="font-display font-semibold text-sm text-txt-primary">
              {lead?.name ?? '…'}
            </h3>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg">
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 md:px-6 md:py-5">
          {isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
          {error && (
            <div className="text-xs text-bear py-4">
              {error.message}
              <button onClick={refetch} className="ml-2 underline text-txt-muted">Retry</button>
            </div>
          )}
          {lead && form && (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.75fr)]">
              {/* Read-only fields */}
              <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  <DetailRow label="Name"    value={lead.name} />
                  <DetailRow label="Email"   value={lead.email} />
                  <DetailRow label="Phone"   value={lead.phone} />
                  <DetailRow label="Broker"  value={lead.broker} />
                  <DetailRow label="Source"  value={lead.source_page} />
                  <DetailRow label="Created" value={fmtDateTime(lead.created_at)} />
                  <DetailRow label="Updated" value={fmtDateTime(lead.updated_at)} />
                  <DetailRow label="Last Contacted" value={lead.last_contacted_at ? fmtDateTime(lead.last_contacted_at) : null} />
                  <DetailRow label="Contacted By (user id)" value={lead.last_contacted_by_user_id != null ? String(lead.last_contacted_by_user_id) : null} />
                  <DetailRow label="IP Address" value={lead.ip_address} />
                </div>
                <DetailRow label="Message" value={lead.message} />
                <DetailRow label="User Agent" value={lead.user_agent} />
              </div>

              <div className="space-y-5 lg:border-l lg:border-bg-border lg:pl-5">
                {/* Editable form */}
                <form onSubmit={handleSave} className="space-y-4">
                  <div>
                    <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest block mb-1">Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      className="input-base text-xs w-full"
                    >
                      {LEAD_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest block mb-1">Internal Notes</label>
                    <textarea
                      value={form.internal_notes}
                      onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))}
                      maxLength={8000}
                      rows={8}
                      className="input-base text-xs w-full resize-y"
                      placeholder="Internal notes…"
                    />
                  </div>

                  <div>
                    <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest block mb-1">Follow-up Date</label>
                    <input
                      type="date"
                      value={form.follow_up_at}
                      onChange={(e) => setForm((f) => ({ ...f, follow_up_at: e.target.value }))}
                      className="input-base text-xs w-full"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={updateLead.isPending}
                    className="btn-primary text-xs py-1.5 w-full"
                  >
                    {updateLead.isPending ? 'Saving…' : 'Save Changes'}
                  </button>
                </form>

                <form onSubmit={handleSendEmail} className="space-y-4 border-t border-bg-border pt-5">
                  <div>
                    <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest block mb-1">Send Email</label>
                    <input
                      type="text"
                      value={emailForm.subject}
                      onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))}
                      maxLength={200}
                      required
                      className="input-base text-xs w-full"
                      placeholder="Subject"
                    />
                  </div>
                  <div>
                    <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest block mb-1">Message</label>
                    <textarea
                      value={emailForm.message}
                      onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))}
                      maxLength={10000}
                      required
                      rows={6}
                      className="input-base text-xs w-full resize-y"
                      placeholder="Write a short reply to this lead…"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sendEmail.isPending}
                    className="btn-primary text-xs py-1.5 w-full"
                  >
                    {sendEmail.isPending ? 'Sending…' : 'Send Email'}
                  </button>
                </form>

                {/* Danger actions */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleMarkSpam}
                    disabled={markSpam.isPending || lead.status === 'SPAM'}
                    className="btn-ghost text-xs py-1.5 border border-bear/30 text-bear hover:bg-bear/10 disabled:opacity-40"
                  >
                    {markSpam.isPending ? 'Marking…' : 'Mark Spam'}
                  </button>
                  <button
                    onClick={handleClose}
                    disabled={closeLead.isPending || lead.status === 'CLOSED'}
                    className="btn-ghost text-xs py-1.5 border border-bg-border disabled:opacity-40"
                  >
                    {closeLead.isPending ? 'Closing…' : 'Close Lead'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// The original Website Leads page — unchanged in behavior, now rendered as one
// tab of the shell below instead of being the page's sole content.
function WebsiteLeadsTab() {
  const [searchText, setSearchText]     = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [fromDate, setFromDate]         = useState('');
  const [toDate, setToDate]             = useState('');
  const [page, setPage]                 = useState(0);
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  const params = useMemo(() => ({
    limit:  PAGE_SIZE,
    offset: page * PAGE_SIZE,
    ...(appliedSearch             && { q:         appliedSearch }),
    ...(statusFilter !== 'ALL'    && { status:     statusFilter }),
    ...(fromDate                  && { date_from:  fromDate }),
    ...(toDate                    && { date_to:    toDate }),
  }), [appliedSearch, statusFilter, fromDate, toDate, page]);

  const { data, isLoading, error, refetch } = useWebsiteLeads(params);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const applySearch = () => {
    setAppliedSearch(searchText.trim());
    setPage(0);
  };

  const handleStatusFilter = (s) => {
    setStatusFilter(s);
    setPage(0);
  };

  return (
    <div className="max-w-6xl space-y-5">
      <SectionHeader
        title="Website Leads"
        subtitle={total > 0 ? `${total} leads` : undefined}
        actions={
          <button onClick={() => refetch()} className="btn-ghost text-xs flex items-center gap-1.5 py-1.5">
            <RefreshCw size={12} /> Refresh
          </button>
        }
      />

      {/* Filters */}
      <div className="panel p-3 space-y-3">
        {/* Search */}
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
            placeholder="Search name, email, broker, message…"
            className="input-base w-72 text-xs"
          />
          <button onClick={applySearch} className="btn-primary text-xs flex items-center gap-1.5 py-1.5">
            <Search size={11} /> Search
          </button>
          {appliedSearch && (
            <button
              onClick={() => { setAppliedSearch(''); setSearchText(''); setPage(0); }}
              className="text-2xs text-txt-muted hover:text-txt-secondary flex items-center gap-1"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>

        {/* Status pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {['ALL', ...LEAD_STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => handleStatusFilter(s)}
              className={cn(
                'px-2 py-1 rounded text-2xs font-mono transition-colors border',
                statusFilter === s
                  ? 'bg-amber/10 border-amber/30 text-amber'
                  : 'border-transparent text-txt-muted hover:text-txt-secondary',
              )}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-2xs font-mono text-txt-muted">From:</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
            className="input-base w-40 text-xs"
          />
          <span className="text-2xs font-mono text-txt-muted">To:</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(0); }}
            className="input-base w-40 text-xs"
          />
          {(fromDate || toDate) && (
            <button
              onClick={() => { setFromDate(''); setToDate(''); setPage(0); }}
              className="text-2xs text-txt-muted hover:text-txt-secondary flex items-center gap-1"
            >
              <X size={11} /> Clear dates
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="panel overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : error ? (
          <div className="flex flex-col items-center py-12 gap-3 text-xs text-bear">
            <p>{error.message}</p>
            <button onClick={() => refetch()} className="btn-ghost text-xs">Retry</button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex justify-center py-12 text-xs text-txt-muted">No leads found.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border">
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted whitespace-nowrap">Created</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Name</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Email</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Phone</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Broker</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Status</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Source</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted whitespace-nowrap">Follow-up</th>
                <th className="py-3 px-4 text-2xs font-mono uppercase text-txt-muted"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((lead) => (
                <tr
                  key={lead.id}
                  onClick={() => setSelectedLeadId(lead.id)}
                  className="border-b border-bg-border/50 hover:bg-bg-hover cursor-pointer transition-colors"
                >
                  <td className="py-3 px-4 font-mono text-txt-muted whitespace-nowrap">
                    {fmtDateTime(lead.created_at)}
                  </td>
                  <td className="py-3 px-4 text-txt-primary font-medium">{lead.name}</td>
                  <td className="py-3 px-4 text-txt-secondary">{lead.email}</td>
                  <td className="py-3 px-4 text-txt-muted">{lead.phone ?? '—'}</td>
                  <td className="py-3 px-4 text-txt-muted">{lead.broker ?? '—'}</td>
                  <td className="py-3 px-4"><StatusBadge status={lead.status} /></td>
                  <td className="py-3 px-4 text-txt-muted max-w-[140px] truncate">{lead.source_page}</td>
                  <td className="py-3 px-4 text-txt-muted whitespace-nowrap">
                    {lead.follow_up_at ? lead.follow_up_at.slice(0, 10) : '—'}
                  </td>
                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setSelectedLeadId(lead.id)}
                      className="btn-ghost text-2xs py-1 px-2 border border-bg-border"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-txt-muted">
          <span>
            Page {page + 1} of {totalPages} ({total} total)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selectedLeadId && (
        <LeadDrawer leadId={selectedLeadId} onClose={() => setSelectedLeadId(null)} />
      )}
    </div>
  );
}

// Tab shell reusing this page's URL (/admin/website/leads) — no new route.
// Tab selection lives in the ?tab= query param so it is bookmarkable/shareable;
// an unrecognized or missing value falls back to Website Leads. Switching tabs
// never touches WebsiteLeadsTab's internal state or behavior above.
function tabFromSearchParams(searchParams) {
  const requested = searchParams.get('tab');
  return PAGE_TABS.some((t) => t.id === requested) ? requested : 'website-leads';
}

export default function AdminWebsiteLeads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = tabFromSearchParams(searchParams);

  const handleTabChange = (tabId) => {
    const next = new URLSearchParams(searchParams);
    if (tabId === 'website-leads') next.delete('tab');
    else next.set('tab', tabId);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-5">
      <Tabs tabs={PAGE_TABS} active={activeTab} onChange={handleTabChange} />
      {activeTab === 'strategy-requests' ? <AdminStrategyRequests /> : <WebsiteLeadsTab />}
    </div>
  );
}
